import { Formik, FieldArray } from 'formik';
import { useRef } from 'react';
import { Trash, UserPlus, MapPin } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import Input from 'components/Profile/Input';
import SelectField from 'components/Profile/SelectField';
import SectionLegend from 'components/Profile/SectionLegend';
import StepShell from 'components/Profile/StepShell';
import { getInitialValues, validationSchema, isEmptyEmergencyContact, formatGooglePlaces, US_STATES } from './helper';
import { formatPhone } from 'utils/helpers';
import { getPractice } from 'library/auth';
import { ProfileActions } from 'store/actions';
import { useJsApiLoader, StandaloneSearchBox } from '@react-google-maps/api'

let enhancer = connect(null, { ...ProfileActions })

const Form = ({ updatePersonalInfo }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"]
  })

  const placeSuggestionsRef = useRef(null)

  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext();
  const initialData = sectionData.personal;
  const practiceInfo = getPractice();
  const hideMiddleName = practiceInfo?.data_provider === 'velox';

  async function handleSubmit({ emergency_contacts, mobile_phone, ...rest }) {

    const filledContacts = emergency_contacts.filter((c) => !isEmptyEmergencyContact(c));

    const remainingIds = filledContacts.filter((c) => c.id).map((c) => c.id);

    const deletedContacts = (initialData?.emergency_contacts?.data ?? [])
      .filter(({ attributes: a }) => !remainingIds.includes(a.id))
      .map(({ attributes: a }) => ({ id: a.id, _destroy: true }));

    try {
      const { data } = await updatePersonalInfo({
        patient: {
          ...rest,
          mobile_phone: mobile_phone.replace(/\D/g, ''),
          emergency_contacts_attributes: [
            ...filledContacts.map((c: any) => ({ ...c, phone: c.phone.replace(/\D/g, '') })),
            ...deletedContacts,
          ],
        },
      });

      toast.success('Personal information saved successfully');
      mergeSection('personal', data);
      goToNextStep('personal');

    } catch (error: any) {
      toast.error(error.errors || 'Failed to save personal information. Please try again later.');
    }
  }

  function handleOnPlacesChanged(setFieldValue) {
    const places = placeSuggestionsRef.current?.getPlaces();
    if (!places || places.length === 0) return;

    const { address_line1, city, state, zip_code } = formatGooglePlaces(places) || {};

    setFieldValue('address_line1', address_line1);
    setFieldValue('city', city);
    setFieldValue('state', state);
    setFieldValue('zip_code', zip_code);
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue }) => (
        <StepShell stepId="personal" isSubmitting={isSubmitting} bodyClassName="space-y-8">
          <div>
            <SectionLegend>Legal name</SectionLegend>
            <div className={hideMiddleName ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-3 gap-4'}>
              <Input
                name="first_name"
                label="First name"
                placeholder="First"
              />

              {!hideMiddleName && (
                <Input
                  name="middle_name"
                  label="Middle"
                  placeholder="Middle"
                  optional
                />
              )}

              <Input
                name="last_name"
                label="Last name"
                placeholder="Last"
              />
            </div>
          </div>

          <div>
            <SectionLegend>Contact information</SectionLegend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                name="email"
                label="Email"
                placeholder="you@example.com"
                disabled={!!initialData?.email}
              />

              <Input
                name="mobile_phone"
                label="Mobile phone"
                placeholder="(000) 000-0000"
                format={formatPhone}
                disabled={!!initialData?.mobile_phone}
              />
            </div>
          </div>

          <div>
            <SectionLegend>Home address</SectionLegend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isLoaded &&
                <div className="sm:col-span-2">
                  <StandaloneSearchBox
                    onLoad={(ref) => placeSuggestionsRef.current = ref}
                    onPlacesChanged={() => handleOnPlacesChanged(setFieldValue)}
                  >
                    <Input
                      name="address_line1"
                      label="Street address"
                      placeholder="Start typing your address..."
                      inputClassName="pl-9"
                      icon={<MapPin size={16} />}
                    />
                  </StandaloneSearchBox>
                </div>
              }

              <Input
                name="address_line2"
                label="Apt / Unit"
                placeholder="Apt 4B"
                optional
              />

              <Input
                name="city"
                label="City"
                placeholder="City"
              />

              <SelectField
                name="state"
                label="State"
                placeholder="Select state"
                options={US_STATES}
              />

              <Input
                name="zip_code"
                label="ZIP code"
                placeholder="00000"
              />
            </div>
          </div>

          <div>
            <FieldArray name="emergency_contacts">
              {({ push, remove, form }) => (
                <div className="space-y-4">
                  <SectionLegend
                    action={form.values.emergency_contacts.length > 0 ? (
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-xs text-base-content/60 font-semibold border border-base-200 rounded-md px-2 py-1.5  hover:text-base-content/70 transition-colors"
                        onClick={() => form.setFieldValue('emergency_contacts', [])}
                      >
                        <Trash className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    ) : null}
                  >
                    Emergency contact
                  </SectionLegend>

                  {form.values.emergency_contacts.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => push({ first_name: '', last_name: '', relationship: '', phone: '' })}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-base-300 bg-base-200/40 hover:bg-base-200 transition-colors text-left group"
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white border border-base-200/80 flex items-center justify-center text-base-content/50 transition-colors">
                        <UserPlus className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-base-content leading-tight">Add an emergency contact</p>
                        <p className="text-xs text-base-content/40 mt-0.5">Someone we can reach if we can't reach you.</p>
                      </div>
                      <span className="flex-shrink-0 text-base-content/30 group-hover:text-base-content/60 transition-colors text-lg leading-none pr-1">+</span>
                    </button>
                  ) : (
                    <>
                      {form.values.emergency_contacts.map((_: any, index: number) => (
                        <div key={index} className={`bg-gray-50 rounded-xl border border-base-200 shadow-sm p-4 space-y-4 ${form.values.emergency_contacts.length === 1 ? 'pb-6' : ''}`}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              name={`emergency_contacts.${index}.first_name`}
                              label="First name"
                              placeholder="First"
                            />

                            <Input
                              name={`emergency_contacts.${index}.last_name`}
                              label="Last name"
                              placeholder="Last"
                            />

                            <Input
                              name={`emergency_contacts.${index}.relationship`}
                              label="Relationship"
                              placeholder="e.g. Spouse, Parent"
                            />

                            <Input
                              name={`emergency_contacts.${index}.phone`}
                              label="Phone"
                              placeholder="(000) 000-0000"
                              format={formatPhone}
                            />
                          </div>
                          {form.values.emergency_contacts.length > 1 &&
                            <div className="flex justify-end pt-1 border-t border-base-200">
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost text-error hover:bg-transparent hover:border-error hover:border"
                                onClick={() => {
                                  remove(index);
                                  toast.success('Emergency contact removed');
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          }
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </FieldArray>
          </div>
        </StepShell>
      )}
    </Formik>
  )
};

export default enhancer(Form);
